import { CELL, GROUND } from '../core/config';
import { createProp, key, MATERIALS, seeded, type Building, type City } from './city';
import { terrainHeight, WATER_LEVEL } from './terrain';

export interface Landscape {
  forest: number[];
  parcels: {
    x: number;
    z: number;
    w: number;
    d: number;
    kind: 'field' | 'orchard' | 'meadow';
    color: number;
  }[];
}

/** Compose the unused existing rim and clear outer lots. Trees and crops use
 * ordinary stable damageable cells; ground colours are terrain surface detail. */
export function prepareLandscape(city: City): City {
  if (city.landscape || !city.terrain?.naturalBoundary) return city;
  const terrain = city.terrain,
    random = seeded(city.seed + 53191),
    landscape: Landscape = (city.landscape = { forest: [], parcels: [] });
  city.props ??= [];
  const occupied = [
    ...city.buildings,
    ...city.props,
    ...(city.infrastructure ?? []),
    ...(city.bridges ?? []),
  ];
  let serial = Math.max(...occupied.map((b) => b.id)) + 1;
  const bins = new Map<string, { x: number; z: number; X: number; Z: number }[]>();
  const reserve = (x: number, z: number, X: number, Z: number) => {
    const box = { x, z, X, Z };
    for (let iz = Math.floor(z / 40); iz <= Math.floor(Z / 40); iz++)
      for (let ix = Math.floor(x / 40); ix <= Math.floor(X / 40); ix++) {
        const tag = `${ix},${iz}`,
          list = bins.get(tag) ?? [];
        list.push(box);
        bins.set(tag, list);
      }
  };
  for (const b of occupied) {
    // Rotated road/rail strips are covered separately by their actual profile.
    if (b.rotation) continue;
    reserve(b.x - 2, b.z - 2, b.x + b.w * CELL + 2, b.z + b.d * CELL + 2);
  }
  for (const e of city.roadGraph?.edges ?? [])
    for (let i = 1; i < (e.profile?.length ?? 0); i++) {
      const a = e.profile![i - 1],
        b = e.profile![i],
        margin = e.width / 2 + (e.pavementWidth ?? 2) + 2;
      reserve(
        Math.min(a.x, b.x) - margin,
        Math.min(a.z, b.z) - margin,
        Math.max(a.x, b.x) + margin,
        Math.max(a.z, b.z) + margin,
      );
    }
  for (const path of city.coast?.paths ?? [])
    for (let i = 1; i < path.points.length; i++) {
      const a = path.points[i - 1],
        b = path.points[i],
        m = path.width / 2 + 1;
      reserve(Math.min(a.x, b.x) - m, Math.min(a.z, b.z) - m, Math.max(a.x, b.x) + m, Math.max(a.z, b.z) + m);
    }
  // Keep the existing railway loop and the finite airport parcel unobstructed.
  reserve(345, -635, 1160, -292);
  for (const [x, z, X, Z] of [
    [-300, -300, 320, -242],
    [-300, 322, 320, 380],
    [-298, -300, -242, 380],
    [262, -300, 320, 380],
  ])
    reserve(x, z, X, Z);
  const clear = (x: number, z: number, w: number, d: number) => {
    for (let iz = Math.floor(z / 40); iz <= Math.floor((z + d) / 40); iz++)
      for (let ix = Math.floor(x / 40); ix <= Math.floor((x + w) / 40); ix++)
        for (const b of bins.get(`${ix},${iz}`) ?? [])
          if (x < b.X && x + w > b.x && z < b.Z && z + d > b.z) return false;
    return true;
  };
  const plantTree = (x: number, z: number, variant: number, ridge: boolean) => {
    const y = terrainHeight(terrain, x + 1.5 * CELL, z + 1.5 * CELL);
    if (y === undefined || y < WATER_LEVEL + 0.08 || !clear(x, z, 3 * CELL, 3 * CELL)) return;
    const tree = createProp(serial++, x, z, 'tree', variant);
    tree.baseY = y;
    tree.district = ridge ? 'woodland' : 'farmland';
    tree.name = ridge ? 'Ridge woodland tree' : 'Orchard fruit tree';
    tree.chunk = `${Math.floor(x / 60)},${Math.floor(z / 60)}`;
    // The foot of a trunk follows its actual sloping triangle instead of floating.
    const root = tree.cells.find((c) => c.material === 'wood' && c.y === 0)!;
    const low =
      Math.min(
        ...[-0.225, 0.225].flatMap((dx) =>
          [-0.225, 0.225].map((dz) => terrainHeight(terrain, x + 1.5 * CELL + dx, z + 1.5 * CELL + dz) ?? y),
        ),
      ) - 0.025;
    const shape = root.bounds!;
    root.bounds = { ...shape, y: (low - y) / CELL, h: shape.h + (y - low) / CELL };
    root.parts = [{ ...root.bounds }];
    city.props!.push(tree);
    reserve(x - 1, z - 1, x + 3 * CELL + 1, z + 3 * CELL + 1);
    if (ridge) landscape.forest.push(tree.id);
  };
  // Unequal groves follow the actual uplands, with gaps at coves and the flight valley.
  for (let z = -1000; z <= 600; z += 27)
    for (let x = -940; x <= 1070; x += 27) {
      if (x > -650 && x < 675 && z > -650) continue;
      const tx = x + (random() - 0.5) * 18,
        tz = z + (random() - 0.5) * 18,
        y = terrainHeight(terrain, tx, tz) ?? -8,
        grove = Math.sin(tx * 0.018 + tz * 0.004) + Math.cos(tz * 0.023 - tx * 0.007);
      if (y < 1.5 || y > 155 || grove < -0.8 || random() < 0.18 || Math.abs(tz + 580) < 100) continue;
      plantTree(tx, tz, Math.floor(random() * 9), true);
    }
  // Leave frontage, pavement, reserved farms and all existing occupied places intact.
  for (let z = -588; z < 610; z += 33)
    for (let x = -586; x < 600; x += 31) {
      if (Math.abs(x) < 220 && Math.abs(z) < 220) continue;
      const w = z < -430 ? 24 : 18,
        d = z < -430 ? 18 : 15;
      if (!clear(x - 1, z - 1, w + 2, d + 2)) continue;
      if (
        [
          [x, z],
          [x + w, z],
          [x, z + d],
          [x + w, z + d],
        ].some(([xx, zz]) => Math.abs((terrainHeight(terrain, xx, zz) ?? -8) - GROUND) > 0.002)
      )
        continue;
      const farm = z < -430 || (x > 340 && z > 380),
        orchard = farm && landscape.parcels.length % 3 === 0,
        kind = orchard ? 'orchard' : farm ? 'field' : 'meadow';
      landscape.parcels.push({ x, z, w, d, kind, color: farm ? (orchard ? 0x9ead81 : 0xb9ac7f) : 0x9eb17e });
      if (orchard) {
        for (let zz = z + 2; zz < z + d - 3; zz += 6)
          for (let xx = x + 2; xx < x + w - 3; xx += 6) plantTree(xx, zz, 0, false);
      } else if (farm) {
        const b: Building = {
          id: serial++,
          name: 'Cultivated field',
          kind: 'planter',
          category: 'prop',
          district: 'farmland',
          x,
          z,
          w: Math.ceil(w / CELL),
          d: Math.ceil(d / CELL),
          h: 1,
          color: 0x99a665,
          accent: 0xc4b678,
          baseY: GROUND,
          exactGeometry: true,
          cells: [],
        };
        for (let iz = 1; iz < b.d - 1; iz += 3)
          for (let ix = 1; ix < b.w - 1; ix += 2)
            b.cells.push({
              id: b.id * 1e6 + key(ix, 0, iz),
              x: ix,
              y: 0,
              z: iz,
              root: true,
              material: 'foliage',
              role: 'prop',
              hp: MATERIALS.foliage.strength,
              color: b.color,
              bounds: { x: 0.2, y: 0, z: 0.35, w: 0.6, h: 0.38, d: 0.3 },
              parts: [
                { x: 0.2, y: 0, z: 0.35, w: 0.6, h: 0.2, d: 0.3 },
                { x: 0.38, y: 0.2, z: 0.42, w: 0.24, h: 0.18, d: 0.16, color: b.accent },
              ],
            });
        city.props.push(b);
      }
      reserve(x, z, x + w, z + d);
    }
  return city;
}
